import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  fetchLikes,
  likeArtist,
  likeTrack,
  unlikeArtist,
  unlikeTrack,
} from '../lib/likesApi';

interface LikesContextValue {
  likedArtists: Set<number>;
  likedTracks: Set<number>;
  isArtistLiked: (artistId: number) => boolean;
  isTrackLiked: (trackId: number) => boolean;
  setArtistLike: (artistId: number, shouldLike: boolean) => Promise<number>;
  setTrackLike: (trackId: number, shouldLike: boolean) => Promise<number>;
  refreshLikes: () => Promise<void>;
  loading: boolean;
  hydrateFromBootstrap: (artists: number[], tracks: number[]) => void;
}

const LikesContext = createContext<LikesContextValue | undefined>(undefined);

export function LikesProvider({ children }: { children: ReactNode }) {
  const { token, user } = useAuth();
  const { pathname } = useLocation();
  const [likedArtists, setLikedArtists] = useState<Set<number>>(new Set());
  const [likedTracks, setLikedTracks] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const skipHydrationRef = useRef(false);
  const hydrateRef = useRef<{ key: string | null; inFlight: boolean }>({
    key: null,
    inFlight: false,
  });

  const hydrate = useCallback(async () => {
    const isAdminRoute = pathname.startsWith('/admin');
    const isReleasesRoute = pathname.startsWith('/releases');
    const isArtistsRoute = pathname === '/artists' || pathname.startsWith('/artists/');
    const isExploreRoute = pathname.startsWith('/explore');
    if (!token || !user || isAdminRoute || isReleasesRoute || isArtistsRoute || isExploreRoute) {
      setLikedArtists(new Set());
      setLikedTracks(new Set());
      setLoading(false);
      return;
    }
    const key = `${token}:${pathname}`;
    if (hydrateRef.current.inFlight || hydrateRef.current.key === key) {
      return;
    }
    hydrateRef.current.inFlight = true;
    hydrateRef.current.key = key;
    setLoading(true);
    try {
      const snapshot = await fetchLikes(token);
      setLikedArtists(new Set((snapshot.artists ?? []).map(Number)));
      setLikedTracks(new Set((snapshot.tracks ?? []).map(Number)));
    } catch (error) {
      console.warn('Failed to load likes', error);
      setLikedArtists(new Set());
      setLikedTracks(new Set());
    } finally {
      setLoading(false);
      hydrateRef.current.inFlight = false;
    }
  }, [pathname, token, user]);

  useEffect(() => {
    const isHomeBootstrap =
      typeof window !== 'undefined' &&
      (window as { __HOME_BOOTSTRAP__?: boolean }).__HOME_BOOTSTRAP__ === true &&
      pathname === '/';
    if (skipHydrationRef.current || isHomeBootstrap) {
      skipHydrationRef.current = false;
      return;
    }
    void hydrate();
  }, [hydrate, pathname]);

  const setArtistLike = useCallback(
    async (artistId: number, shouldLike: boolean) => {
      if (!token || !user) {
        throw new Error('AUTH_REQUIRED');
      }
      const response = shouldLike
        ? await likeArtist(artistId, token)
        : await unlikeArtist(artistId, token);
      setLikedArtists((prev) => {
        const next = new Set(prev);
        if (shouldLike) {
          next.add(artistId);
        } else {
          next.delete(artistId);
        }
        return next;
      });
      return response.likes_count ?? 0;
    },
    [token, user],
  );

  const setTrackLike = useCallback(
    async (trackId: number, shouldLike: boolean) => {
      if (!token || !user) {
        throw new Error('AUTH_REQUIRED');
      }
      const response = shouldLike
        ? await likeTrack(trackId, token)
        : await unlikeTrack(trackId, token);
      setLikedTracks((prev) => {
        const next = new Set(prev);
        if (shouldLike) {
          next.add(trackId);
        } else {
          next.delete(trackId);
        }
        return next;
      });
      return response.likes_count ?? 0;
    },
    [token, user],
  );

  const value = useMemo<LikesContextValue>(
    () => ({
      likedArtists,
      likedTracks,
      isArtistLiked: (artistId: number) => likedArtists.has(artistId),
      isTrackLiked: (trackId: number) => likedTracks.has(trackId),
      setArtistLike,
      setTrackLike,
      refreshLikes: hydrate,
      loading,
      hydrateFromBootstrap: (artists: number[], tracks: number[]) => {
        skipHydrationRef.current = true;
        setLikedArtists(new Set(artists.map(Number)));
        setLikedTracks(new Set(tracks.map(Number)));
        setLoading(false);
      },
    }),
    [hydrate, likedArtists, likedTracks, loading, setArtistLike, setTrackLike],
  );

  return <LikesContext.Provider value={value}>{children}</LikesContext.Provider>;
}

export function useLikes(): LikesContextValue {
  const ctx = useContext(LikesContext);
  if (!ctx) {
    throw new Error('useLikes must be used within a LikesProvider');
  }
  return ctx;
}
